import { Component } from "@angular/core";
import { MatDialog } from '@angular/material/dialog';

import { BenchmarkSeries, BenchmarkSeriesItem, BenchmarkSeriesModel } from "src/app/shared/benchmarks/benchmark-series";
import { DataService } from "src/app/shared/network/data.service";
import { NotificationService } from "src/app/shared/notificaton.service";
import { ProjectService } from "src/app/shared/project/project.service";
import { RenameModalComponent } from './rename-modal.component';
import { BaseImportStepComponent, ImportStepState } from "../base-import-step.component";
import { ImportStep } from "../import-step";

export class Coord {
    public row: number;
    public col: number;
}

@Component({
    selector: 'series',
    templateUrl: './series.component.html',
    styleUrls: ['../general.scss', './series.component.scss']
})
export class SeriesComponent extends BaseImportStepComponent {
    readonly step = ImportStep.Series;
    public loading = true;
    public aiLoading = false;
    public aiLoadingCancelled = false;
    public showHint = true;
    public oldControlValue: string;
    public newControlValue: string;
    public series: BenchmarkSeries[];               //in GUI now
    private seriesModelInEdit: BenchmarkSeriesModel; //were in GUI, edited or not
    private savedSeriesModel: BenchmarkSeriesModel; //default or from db
    private aiSeriesModel: BenchmarkSeriesModel;    //from AI
    public movingItem: Coord;
    public multiSelectStartedAt: Coord;
    public multiSelectEndedAt: Coord;
    public selectedSeriesName: string;
    public differentSeriesSelected: boolean;
    public previousSelectedSeries: string;

    constructor(private dataService: DataService, projectService: ProjectService, private notificationService: NotificationService,
        public dialog: MatDialog) {
        super(projectService);
    }

    
    public async loadDataInternal(): Promise<void> {
        let res = await this.dataService.get<BenchmarkSeriesModel>(`benchmarks/series/${this.projectId}`);
        this.stepState = res.stepState;
        if (this.stepState <= ImportStepState.NotAvailableYet) {
            return;
        }
        this.savedSeriesModel = JSON.parse(JSON.stringify(res));
        this.seriesModelInEdit = JSON.parse(JSON.stringify(res));
        this.series = res.series;
        this.markItemsForMultiSelect();
        this.oldControlValue = res.oldControlValue;
        this.newControlValue = res.newControlValue;
        this.selectedSeriesName = res.selectedSeriesName;
        this.differentSeriesSelected = res.differentSeriesSelected;
        this.previousSelectedSeries = res.previousSelectedSeries;
        this.loading = false;
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    async saveSeries() {
        if (this.loading) {
            return;
        }
        try {
            const changesApplied = await this.dataService.post<boolean>('benchmarks/saveSeries', {
                projectId: this.projectId,
                series: this.series,
                selectedSeriesName: this.selectedSeriesName
            });
            this.finishStepSuccessfully(changesApplied);
        } finally {
            this.loading = false;
        }
    }

    startMovingItem(row: number, column: number): void {
        this.movingItem = {
            row: row,
            col: column,
        };
    }

    swapWith(row: number, col: number): void {
        if (this.movingItem) {
            let tmp = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = this.series[row].items[col];
            this.series[row].items[col] = tmp;
            this.cancelMovingItem();
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            for (let i = 0; i < count; ++i) {
                let tmp = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = this.series[row].items[i + col];
                this.series[row].items[i + col] = tmp;
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertLeftFrom(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(col, 0, null);
            }
            this.series[row].items[col] = selected;
            this.cancelMovingItem();
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            let selected = [];
            for (let i = 0; i < count; ++i) {
                selected.push(this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i]);
                this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i] = null;
            }
            for (let i = 0; i < count; ++i) {
                for (let j = 0; j < this.series.length; ++j) {
                    this.series[j].items.splice(col, 0, null);
                }
            }
            for (let i = 0; i < count; ++i) {
                this.series[row].items[i + col] = selected[i];
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertRightFrom(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(col + 1, 0, null);
            }
            this.series[row].items[col + 1] = selected;
            this.cancelMovingItem();
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            let selected = [];
            for (let i = 0; i < count; ++i) {
                selected.push(this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i]);
                this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i] = null;
            }
            for (let j = 0; j < this.series.length; ++j) {
                for (let i = 0; i < count; ++i) {
                    this.series[j].items.splice(col + 1, 0, null);
                }
            }
            for (let i = 0; i < count; ++i) {
                this.series[row].items[1 + i + col] = selected[i];
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertAbove(row: number, col: number): void {
        if (this.series[row].isExclude) {
            return;
        }
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            this.series.splice(row, 0, this.createNewSeries(this.series));
            this.series[row].items[col] = selected;
            this.cancelMovingItem();
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let newSeries = this.createNewSeries(this.series);
            for (let i = 0; i <= (this.multiSelectEndedAt.col - this.multiSelectStartedAt.col); ++i) {
                newSeries.items[i + col] = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = null;
            }
            this.series.splice(row, 0, newSeries);
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertBelow(row: number, col: number): void {
        if (this.series[row].isExclude) {
            return;
        }
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            this.series.splice(row + 1, 0, this.createNewSeries(this.series));
            this.series[row + 1].items[col] = selected;
            this.cancelMovingItem();
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let newSeries = this.createNewSeries(this.series);
            for (let i = 0; i <= (this.multiSelectEndedAt.col - this.multiSelectStartedAt.col); ++i) {
                newSeries.items[i + col] = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = null;
            }
            this.series.splice(row + 1, 0, newSeries);
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    public get forecastItemsSplit(): boolean {
        if (!this.series) {
            return false;
        }
        let rowWithForecast = -1;
        for (let i = 0; i < this.series.length; ++i) {
            for (let item of this.series[i].items) {
                if (item && item.isSelectedAsForecast) {
                    if (rowWithForecast == -1) {
                        rowWithForecast = i;
                    } else if (rowWithForecast != i) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    private addColumnsIfRequired(targetIx: number) {
        //add columns if required on bulk movement
        let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1
        let columnsToAdd = count + targetIx - this.series[0].items.length;
        for (let i = 0; i < columnsToAdd; ++i) {
            for (let j = 0; j < this.series.length; ++j) {
                this.series[j].items.splice(this.series[j].items.length, 0, null);
            }
        }
    }

    createNewSeries(series: BenchmarkSeries[]): BenchmarkSeries {
        let newSeries = new BenchmarkSeries();
        let name: string;
        for (let i = 1; i <= series.length + 1; ++i) {
            name = "Series " + i;
            let exists = false;
            for (let j = 0; j < series.length; ++j) {
                if (series[j].name == name) {
                    exists = true;
                    break;
                }
            }
            if (!exists) {
                break;
            }
        }
        newSeries.name = name;
        newSeries.items = [];
        for (let i = 0; i < series[0].items.length; ++i) {
            newSeries.items.push(null);
        }
        return newSeries;
    }

    removeEmptyRowsColumns(): void {
        let emptyRows = this.getEmptyRows(this.series);
        const nonExcludedEmptyRows = emptyRows.filter(ix => !this.series[ix].isExclude);
        const lastNonExcludedEmptyRow = nonExcludedEmptyRows.length > 0 ? nonExcludedEmptyRows[nonExcludedEmptyRows.length - 1] : -1;
        for (let k = emptyRows.length - 1; k >= 0; --k) {
            let ix = emptyRows[k];
            if (!this.series[ix].isExclude && ix !== lastNonExcludedEmptyRow) {
                this.series.splice(ix, 1);
            }
        }
        let emptyCols = this.getEmptyCols(this.series);
        for (let k = emptyCols.length - 1; k >= 0; --k) {
            let ix = emptyCols[k];
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(ix, 1);
            }
        }
    }

    private getEmptyRows(series: BenchmarkSeries[]): number[] {
        let emptyRows = [];
        for (let i = 0; i < series.length; ++i) {
            let allEmpty = true;
            for (let j = 0; j < series[i].items.length; ++j) {
                if (series[i].items[j] && series[i].items[j].controlValue) {
                    allEmpty = false;
                    break;
                }
            }
            if (allEmpty) {
                emptyRows.push(i);
            }
        }
        emptyRows.sort();
        return emptyRows;
    }

    private getEmptyCols(series: BenchmarkSeries[]): number[] {
        let emptyCols = [];
        for (let j = 0; j < series[0].items.length; ++j) {
            let allEmpty = true;
            for (let i = 0; i < series.length; ++i) {
                if (series[i].items[j] && series[i].items[j].controlValue) {
                    allEmpty = false;
                    break;
                }
            }
            if (allEmpty) {
                emptyCols.push(j);
            }
        }
        return emptyCols;
    }

    private markItemsForMultiSelect() {
        for (let ser of this.series) {
            if (ser.items.length < 2) {
                return;
            }
            for (let i = 0; i < ser.items.length; ++i) {
                if (!ser.items[i]) {
                    continue;
                }
                if (i == 0) {
                    if (ser.items[i + 1]) {
                        ser.items[i].canStartMultiSelect = true;
                    }
                } else {
                    if (ser.items[i - 1]) {
                        ser.items[i].canStartMultiSelect = true;
                    }
                }
            }
        }
    }

    public startMultiSelect(rowIx: number, colIx: number) {
        this.cancelMovingItem();
        this.multiSelectStartedAt = {
            row: rowIx,
            col: colIx,
        }
        this.series[rowIx].items[colIx].isMultiSelected = true;

        for (let i = colIx - 1; i >= 0; --i) {
            if (!this.series[rowIx].items[i]) {
                break;
            }
            this.series[rowIx].items[i].canEndMultiSelect = true;
        }
        for (let i = colIx + 1; i < this.series[rowIx].items.length; ++i) {
            if (!this.series[rowIx].items[i]) {
                break;
            }
            this.series[rowIx].items[i].canEndMultiSelect = true;
        }
    }

    public cancelMultiSelect() {
        for (let ser of this.series) {
            for (let item of ser.items) {
                if (item) {
                    item.isMultiSelected = false;
                    item.canEndMultiSelect = false;
                }
            }
        }
        this.multiSelectStartedAt = undefined;
        this.multiSelectEndedAt = undefined;
    }

    public cancelMovingItem() {
        this.movingItem = undefined;
    }

    public endMultiSelect(rowIx: number, colIx: number) {
        if (!this.multiSelectStartedAt || this.multiSelectStartedAt.row != rowIx) {
            //something is wrong
            this.cancelMultiSelect();
            this.markItemsForMultiSelect();
            return;
        }

        let minColIx = Math.min(colIx, this.multiSelectStartedAt.col);
        let maxColIx = Math.max(colIx, this.multiSelectStartedAt.col);
        this.multiSelectStartedAt = {
            row: rowIx,
            col: minColIx,
        };
        this.multiSelectEndedAt = {
            row: rowIx,
            col: maxColIx,
        };
        for (let i = minColIx; i <= maxColIx; ++i) {
            this.series[rowIx].items[i].isMultiSelected = true;
        }
    }

    public toggleSeriesSelectionOld(seriesName: string, $event: Event) {
        console.log($event);
        $event.preventDefault();
        console.log(this.selectedSeriesName);
        this.selectedSeriesName = seriesName;
        console.log(this.selectedSeriesName);
    }

    public toggleSeriesSelection(seriesName: string) {
        this.selectedSeriesName = seriesName;
    }

    showRenameDialog(rowIx: number) {
        const dialogRef = this.dialog.open(RenameModalComponent, {
            data: {
                name: this.series[rowIx].name,
            },
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result !== undefined) {
                let series = this.series[rowIx]
                for (let i = 0; i < this.series.length; ++i) {
                    if (series === this.series[i]) {
                        continue;
                    }
                    if (result == this.series[i].name) {
                        this.notificationService.showError("Series name must be unique.");
                        return;
                    }
                }
                if (this.selectedSeriesName == series.name) {
                    this.selectedSeriesName = result;
                }
                series.name = result;
            }
        });
    }

    public async loadAISeries() {
        if (this.aiSeriesModel) {
            this.switchToAISeries();
            return;
        }
        if (this.aiLoading) {
            return;
        }
        try { 
            this.aiLoading = true;
            this.aiSeriesModel = await this.dataService.get<BenchmarkSeriesModel>(`benchmarks/aiSeries/${this.projectId}`);
            if (!this.aiLoadingCancelled) {
                this.switchToAISeries();
            }
        } finally {
            this.aiLoading = false;
        }
    }

    public cancelAILoading(): void {
        this.aiLoadingCancelled = true;
        this.aiLoading = false;
    }

    public cancelClick(): void {
        if (this.movingItem) {
            this.cancelMovingItem();
        } else {
            this.revertToSavedSeries();
        }
    }

    public revertToSavedSeries(): void {
        this.cancelMultiSelect();
        this.series = JSON.parse(JSON.stringify(this.savedSeriesModel.series));
        this.selectedSeriesName = this.savedSeriesModel.selectedSeriesName;
    }

    private switchToAISeries(): void {
        if (!this.aiSeriesModel || !this.aiSeriesModel.series || !this.aiSeriesModel.series.length) {
            return;
        }
        this.cancelMultiSelect();
        this.series = JSON.parse(JSON.stringify(this.aiSeriesModel.series));
        this.selectedSeriesName = this.aiSeriesModel.selectedSeriesName;
    }

    public get isAiSeriesActive(): boolean {
        if (!this.aiSeriesModel?.series || !this.series) {
            return false;
        }
        
        if (this.aiSeriesModel.series.length !== this.series.length) {
            return false;
        }
    
        return this.aiSeriesModel.series.every((aiSeries, seriesIndex) => {
            const currentSeries = this.series[seriesIndex];
            
            // Compare series properties
            if (aiSeries.name !== currentSeries.name || 
                aiSeries.isExclude !== currentSeries.isExclude ||
                aiSeries.items.length !== currentSeries.items.length) {
                return false;
            }
    
            // Compare items in the series
            return aiSeries.items.every((item, itemIndex) => {
                const currentItem = currentSeries.items[itemIndex];
                
                // If both items are null/undefined, they're equal
                if (!item && !currentItem) {
                    return true;
                }
                
                // If one is null/undefined but not the other, they're not equal
                if (!item || !currentItem) {
                    return false;
                }
    
                // Compare BenchmarkSeriesItem properties
                return item.controlValue === currentItem.controlValue;
            });
        });
    }

    public get hasForecastItems(): boolean {
        return !!this.series && this.series.some(s => s.items.some(item => item && item.isSelectedAsForecast));
    }

    public get nonExcludedSeriesCount(): number {
        return this.series ? this.series.filter(s => !s.isExclude).length : 0;
    }

    public get allItemsExcluded(): boolean {
        if (!this.series) return false;
        return !this.series.some(s => !s.isExclude && s.items.some(item => item && item.controlValue));
    }

    public get otherSeriesNames(): string {
        if (!this.series) return '';
        return this.series
            .filter(s => !s.isExclude && s.name !== this.selectedSeriesName)
            .map(s => s.name)
            .join(', ');
    }
}
